import { createAction, props } from '@ngrx/store';

import { ROUTE_NAMES } from '@constants';
import {
  AcceptedResourceItemType,
  AnyResource,
  Billing,
  Breadcrumb,
  CalendarView,
  CommunityState,
  CommunitySubscription,
  Conversation,
  ConversationListElement,
  Message,
  RootState,
  SubscriptionPlanDetails,
  SystemSettings,
  UserSettings,
} from '@models';
import { LastSearch, SearchParams } from './initial-state';

export const setBreadcrumbs = createAction(
  '[Breadcrumbs] Set',
  props<{ payload: Breadcrumb[] }>()
);

export const setBilling = createAction(
  '[Billing] Set',
  props<{ payload: Billing }>()
);
export const updateBilling = createAction(
  '[Billing] Update',
  props<{ payload: Partial<Billing> }>()
);
export const setSubscription = createAction(
  '[Billing] Set Subscription',
  props<{ payload: CommunitySubscription }>()
);
export const updateSubscription = createAction(
  '[Billing] Update Subscription',
  props<{
    payload: Partial<CommunitySubscription>;
  }>()
);
export const setPlanDetails = createAction(
  '[Billing] Set Plan Details',
  props<{ payload: SubscriptionPlanDetails }>()
);
export const resetPlanDetails = createAction('[Billing] Reset Plan Details');

export const setSelectedConversation = createAction(
  '[Conversation] Set Selected',
  props<{ payload: Nullable<Conversation> }>()
);
export const setMessages = createAction(
  '[Conversation] Set Messages',
  props<{ payload: Selectable<Message>[] }>()
);
export const setConversationList = createAction(
  '[Conversation] Set List',
  props<{ payload: ConversationListElement[] }>()
);
export const setIsParticipant = createAction(
  '[Conversation] Set Is Participant',
  props<{ payload: boolean }>()
);
export const setIsOwner = createAction(
  '[Conversation] Set Is Owner',
  props<{ payload: boolean }>()
);
export const setUpdateDate = createAction(
  '[Conversation] Set Update Date',
  props<{ payload: number }>()
);
export const deleteConversation = createAction(
  '[Conversation] Delete Conversation',
  props<{ payload: number }>()
);

export const setList = createAction(
  '[List] Set',
  props<{ payload: AcceptedResourceItemType[] }>()
);
export const emptyList = createAction('[List] Empty');
export const addSelectedItems = createAction(
  '[List] Add Selected',
  props<{ payload: AnyResource[] }>()
);
export const removeSelectedItems = createAction(
  '[List] Remove Selected',
  props<{ payload: AnyResource[] }>()
);
export const setSelectedItems = createAction(
  '[List] Set Selected',
  props<{ payload: AnyResource[] }>()
);
export const emptySelectedItems = createAction('[List] Empty Selected');
export const setIsAllSelected = createAction(
  '[List] Set Is All Selected',
  props<{ payload: boolean }>()
);

export const setSystemSettings = createAction(
  '[Settings] Set System',
  props<{ payload: SystemSettings }>()
);
export const setSigningRoomConfig = createAction(
  '[Settings] Set Signing Room Config',
  props<{ payload: Record<string, unknown> }>()
);
export const setAreZippedFilenamesShortened = createAction(
  '[Settings] Set Are Zipped Filenames Shortened',
  props<{ payload: boolean }>()
);

export const setIsModalOpen = createAction(
  '[UI] Set Is Modal Open',
  props<{ payload: boolean }>()
);
export const setIsModalFired = createAction(
  '[UI] Set Is Modal Fired',
  props<{ payload: boolean }>()
);

export const setLocale = createAction(
  '[Locale] Set',
  props<{ payload: string }>()
);

export const setLanguages = createAction(
  '[Languages] Set',
  props<{ payload: string[] }>()
);

export const setRequestedUrl = createAction(
  '[URL] Set Requested URL',
  props<{ payload: string }>()
);

export const clearRequestedUrl = createAction('[URL] Clear Requested URL');

export const setIsLoggedIn = createAction(
  '[Auth] Set Is Logged In',
  props<{ payload: boolean }>()
);
export const setSessionToken = createAction(
  '[Auth] Set Session Token',
  props<{ payload: Nullable<string> }>()
);
export const setIsUserSessionExpiration = createAction(
  '[Auth] Set Is User Session Expiration',
  props<{ payload: boolean }>()
);

export const setNavigationRoute = createAction(
  '[Navigation] Set Route',
  props<{ payload?: ROUTE_NAMES }>()
);
export const setNavigationOpen = createAction(
  '[Navigation] Set Open',
  props<{ payload: boolean }>()
);

export const setDefaultView = createAction(
  '[Views] Set Default View',
  props<{ payload: number }>()
);
export const setUsersView = createAction(
  '[Views] Set Users View',
  props<{ payload: number }>()
);
export const setGroupsView = createAction(
  '[Views] Set Groups View',
  props<{ payload: number }>()
);
export const setRoomTreeView = createAction(
  '[Views] Set Room Tree View',
  props<{ payload: number }>()
);
export const setCalendarView = createAction(
  '[Views] Set Calendar View',
  props<{ payload: CalendarView }>()
);
export const resetViews = createAction('[Views] Reset');

export const setUserData = createAction(
  '[User] Set Data',
  props<{ payload: UserSettings }>()
);
export const updateUserData = createAction(
  '[User] Update Data',
  props<{ payload: Partial<UserSettings> }>()
);
export const setUserPreferences = createAction(
  '[User] Set Preferences',
  props<{ payload: Partial<NonNullable<UserSettings['preferences']>> }>()
);
export const resetUser = createAction('[User] Reset');

export const setRootData = createAction(
  '[Root] Set Data',
  props<{ payload: RootState }>()
);
export const resetRoot = createAction('[Root] Reset');

export const setCommunityData = createAction(
  '[Community] Set Data',
  props<{ payload: CommunityState }>()
);
export const resetCommunity = createAction('[Community] Reset');

export const setIsSidebarExpanded = createAction(
  '[Sidebar] Set Is Expanded',
  props<{ payload: boolean }>()
);
export const toggleIsSidebarExpanded = createAction(
  '[Sidebar] Toggle Is Expanded'
);
export const setSidebarVisibility = createAction(
  '[Sidebar] Set Visibility',
  props<{ payload: boolean }>()
);
export const toggleSidebarVisibility = createAction(
  '[Sidebar] Toggle Visibility'
);

export const setLastSearch = createAction(
  '[Search] Set Last Search',
  props<{ payload: Nullable<LastSearch> }>()
);
export const setSearchQuery = createAction(
  '[Search] Set Query',
  props<{ payload: string }>()
);
export const setSearchParams = createAction(
  '[Search] Set Params',
  props<{ payload: SearchParams }>()
);
export const resetSearchParams = createAction('[Search] Reset Params');
export const updateSearchParams = createAction(
  '[Search] Update Params',
  props<{ payload: Partial<SearchParams> }>()
);
export const setOriginPath = createAction(
  '[Search] Set Origin Path',
  props<{ payload: Nullable<string> }>()
);
export const setIsSearchScopedByResource = createAction(
  '[Search] Set Resource Scope',
  props<{ payload: boolean }>()
);
export const resetIsSearchScopedByResource = createAction(
  '[Search] Reset Resource Scope'
);

export const setCurrentResource = createAction(
  '[Resource] Set Current Resource',
  props<{ payload: Partial<AnyResource> }>()
);
export const resetCurrentResource = createAction('[Resource] Reset Current');
export const updateCurrentResource = createAction(
  '[Resource] Update Current Resource',
  props<{ payload: Partial<AnyResource> }>()
);
